import { useEffect, useState } from 'react'
import {
  BoardLayout,
  Column,
  ColumnContent,
  ColumnCount,
  ColumnHeader,
  ColumnTitle,
  EmptyState,
  InventoryLeft,
  InventoryName,
  InventoryQty,
  InventoryRow,
  ItemsRow,
  OrderCard,
  PatientAvatar,
  PatientAvatarShell,
  PatientBlock,
  PatientItemCount,
  PatientName,
  PatientText,
  ProductChip,
  ProductDetails,
  ProductName,
  ProductQty,
  ProductThumb,
  ProductThumbPlaceholder,
} from './ShopTab.styled'
import { useShopPatientProfilePics } from '../../hooks/useShopPatientProfilePics'
import DefaultAvatar from '../../images/ghost.svg'
import { getStudioPickupBoardAsync } from '../../redux/shop'
import { selectShopBoardForDisplay, selectShopInventoryForDisplay } from '../../redux/shop/selectors'
import { useAppDispatch, useAppSelector } from '../../redux/store'

function ProductImage({ src, alt }: { src?: string; alt: string }) {
  const [failed, setFailed] = useState(false)

  if (!src || failed) return <ProductThumbPlaceholder />

  return (
    <ProductThumb
      src={src}
      alt={alt}
      onError={() => {
        setFailed(true)
      }}
    />
  )
}

const ShopTab = () => {
  const dispatch = useAppDispatch()
  const studioClinicId = useAppSelector((state) => state.studioAuth.studioDetails?.id)
  const legacyClinicId = useAppSelector((state) => state.studio.clinic?.id)
  const clinicId = studioClinicId || legacyClinicId
  const board = useAppSelector(selectShopBoardForDisplay)
  const inventory = useAppSelector(selectShopInventoryForDisplay)
  const lobbyProfilePics = useShopPatientProfilePics()

  const inventoryTotal = inventory.reduce((sum, item) => sum + item.quantity, 0)

  useEffect(() => {
    if (!clinicId) return
    dispatch(getStudioPickupBoardAsync({ clinicId }))
    const interval = setInterval(() => {
      dispatch(getStudioPickupBoardAsync({ clinicId }))
    }, 5000)
    return () => clearInterval(interval)
  }, [clinicId, dispatch])

  return (
    <BoardLayout>
      <Column $wide>
        <ColumnHeader>
          <ColumnTitle>Orders</ColumnTitle>
          <ColumnCount>{board.count}</ColumnCount>
        </ColumnHeader>
        <ColumnContent>
          {board.orders.map((order) => {
            const avatarSrc = order.profilePic || lobbyProfilePics.get(order.patientId) || DefaultAvatar

            return (
              <OrderCard key={order.patientId}>
                <PatientAvatarShell>
                  <PatientAvatar
                    src={avatarSrc}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.onerror = null
                      e.currentTarget.src = DefaultAvatar
                    }}
                  />
                </PatientAvatarShell>
                <PatientBlock>
                  <PatientText>
                    <PatientName>
                      {order.firstname || ''} {order.lastname || ''}
                    </PatientName>
                    <PatientItemCount>
                      {order.itemCount} {order.itemCount === 1 ? 'item' : 'items'}
                    </PatientItemCount>
                  </PatientText>
                </PatientBlock>
                <ItemsRow>
                  {order.items.map((item) => (
                    <ProductChip key={item.productId}>
                      <ProductImage src={item.imageUrl} alt="" />
                      <ProductDetails>
                        <ProductName>{item.name}</ProductName>
                        <ProductQty>{item.quantity}</ProductQty>
                      </ProductDetails>
                    </ProductChip>
                  ))}
                </ItemsRow>
              </OrderCard>
            )
          })}
        </ColumnContent>
      </Column>

      <Column>
        <ColumnHeader>
          <ColumnTitle>Inventory</ColumnTitle>
          <ColumnCount>{inventoryTotal}</ColumnCount>
        </ColumnHeader>
        <ColumnContent>
          {inventory.length === 0 && <EmptyState>Nothing to pull.</EmptyState>}
          {inventory.map((item) => (
            <InventoryRow key={item.productId}>
              <InventoryLeft>
                <ProductImage src={item.imageUrl} alt="" />
                <InventoryName>{item.name}</InventoryName>
              </InventoryLeft>
              <InventoryQty>{item.quantity}</InventoryQty>
            </InventoryRow>
          ))}
        </ColumnContent>
      </Column>
    </BoardLayout>
  )
}

export default ShopTab
